import { useMutation } from "@apollo/client";
import { TextField } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { gql } from "generated-graphql";
import { useState } from "react";

const DISMISS_RULE_SUGGESTION = gql(`
  mutation DismissRuleSuggestion($id: ID!, $reason: String) {
    dismissRuleSuggestion(id: $id, reason: $reason) {
      id
      status
      dismissedAt
      dismissedBy
      dismissReason
    }
  }
`);

type Props = {
  open: boolean;
  suggestionId: string;
  onClose: () => void;
  /** Called after a successful dismissal (dialog closes itself first). */
  onDone: () => void;
};

/**
 * Dismiss a rule suggestion so producers stop resurfacing it. The reason is
 * optional — a bare dismissal is valid — and can be reopened later.
 */
export function DismissSuggestionDialog({ open, suggestionId, onClose, onDone }: Props) {
  const [reason, setReason] = useState("");
  const alerts = useAlerts();

  const [dismiss, { loading }] = useMutation(DISMISS_RULE_SUGGESTION, {
    refetchQueries: ["RuleSuggestions"],
    // Hold the dialog's loading state until the grid actually drops the row so
    // the action reads as "done" rather than closing before anything changes.
    awaitRefetchQueries: true,
  });

  const handleClose = () => {
    setReason("");
    onClose();
  };

  const handleSubmit = async () => {
    const trimmed = reason.trim();
    try {
      await dismiss({ variables: { id: suggestionId, reason: trimmed === "" ? null : trimmed } });
      alerts.success("Suggestion dismissed.");
      handleClose();
      onDone();
    } catch (e) {
      alerts.error("Failed to dismiss the suggestion.", e);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      slotProps={{ paper: { sx: { width: 560, maxWidth: "90vw" } } }}
      title="Dismiss suggestion"
      description="Dismiss this rule idea. Producers won't resurface it; you can reopen it later."
      primaryActionButtonProps={{
        onClick: handleSubmit,
        loading,
        loadingText: "Dismissing...",
        hideStartIcon: true,
        saveText: "Dismiss suggestion",
        disabled: loading,
      }}
      cancelButtonProps={{ disabled: loading }}
    >
      <TextField
        label="Reason (optional)"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        size="small"
        multiline
        minRows={2}
        fullWidth
        autoFocus
        sx={{ mt: 1 }}
      />
    </Dialog>
  );
}
